/**
 * WalletPassBadge — the "Add to Apple Wallet" control, as Apple's own artwork.
 *
 * WHY THIS IS A COMPONENT AND NOT A BUTTON WITH A WALLET ICON
 *
 * "Add to Apple Wallet" is not a label we may set on an ordinary button. It is
 * a badge Apple licenses, ships as artwork per locale, and publishes rules for:
 * the wordmark and logo may not be redrawn, recoloured, rotated, shadowed or
 * animated, and the badge needs clear space around it. A hand-drawn look-alike
 * on an outline button is the failure mode this component exists to prevent —
 * so the artwork is served byte-identical from the static tree, and nothing in
 * here touches its pixels.
 *
 * Lifted from the registration confirmation panel (`landing-builder-helpers`,
 * PR #842) so both surfaces render the SAME control rather than each growing
 * its own approximation. The comments below are that implementation's, kept
 * because every one of them records a real defect.
 *
 * MISSING ARTWORK FALLS BACK TO `fallback`, NEVER TO A BROKEN IMAGE OR A
 * HAND-DRAWN BADGE. Locales Apple has not shipped us yet fall to `en`; if that
 * is missing too, the surface gets its ordinary wallet control and picks up the
 * real badge the moment the file lands, with no code change.
 */
import { useEffect, useRef, useState, type JSX, type ReactNode } from "react";
import { currentLocale } from "./i18n";
import { manageSurface } from "@v0y4ge/api-client";

import { useT } from "@v0y4ge/i18n-runtime";
/** WHERE THE ARTWORK IS SERVED FROM.
 *
 *  Production: the app owns its origin, so the badge is at `/wallet-badge/…`.
 *  A path RELATIVE to the document would be wrong there — the designer runs at
 *  `/journeys/confirmation`, which would resolve to `/journeys/wallet-badge/…`.
 *  Root-absolute is the only correct production answer.
 *
 *  The design harness is the exception: it serves the whole project under
 *  `…/v1/design/projects/<id>/serve/`, and a root-absolute URL skips that prefix
 *  and 404s — which is precisely how this badge silently fell back to its plain
 *  control after the artwork had already been added. The prefix is read off the
 *  document URL AS LOADED (the navigation entry survives the SPA's own routing,
 *  which `document.baseURI` does not), so nothing has to be configured and
 *  production is untouched. */
const assetBase = (): string => {
  if (typeof window === "undefined") return "/";
  const override = (window as unknown as { __CD_ASSET_BASE__?: string }).__CD_ASSET_BASE__;
  if (typeof override === "string" && override) return override;
  const nav = performance.getEntriesByType("navigation")[0] as { name?: string } | undefined;
  const served = nav && nav.name ? /^(.*\/serve\/)/.exec(nav.name) : null;
  return served ? served[1] : `${window.location.origin}/`;
};
const badgeUrl = (name: string) => {
  const base = assetBase();
  const path = `wallet-badge/${name}.svg`;
  return base === "/" ? `/${path}` : new URL(path, base).toString();
};

/** Apple publishes no absolute minimum; 44px is our touch-target floor, and the
 *  width follows the artwork's own 3.163 ratio (139.2px). Clear space is the
 *  published 0.1×height, written against the same constant so the two move
 *  together if the height ever changes. */
const BADGE_H = 44;

/** iPhone / iPad Safari opens the Wallet sheet on navigation to a .pkpass; a blob saved
 *  with `download` does not. iPadOS reports as a Mac, hence the touch check. */
const opensWalletSheet = (): boolean => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1);
};

type PassPhase = "idle" | "busy" | "done" | "failed";

/** Fetch the pass first so the tap has states (CC 0313): busy from the tap, a line when a
 *  desktop has saved the file, and a failure the person can retry. The signed URL is fetched
 *  with no referrer, as the anchor did. On iPhone and iPad the fetch only gives the states;
 *  the Wallet sheet still comes from a navigation to the link, never from a blob.
 *  When the fetch itself cannot run (network down, or the pass host not answering CORS for
 *  this page), it falls back to today's plain navigation, so the control never ends up worse
 *  than before. That fallback has no end state, because nothing reports one. */
function usePassDownload(href?: string, mint?: () => Promise<string | null>) {
  const [phase, setPhase] = useState<PassPhase>("idle");
  const gone = useRef(false);
  useEffect(() => () => { gone.current = true; }, []);
  useEffect(() => { setPhase("idle"); }, [href]);
  const set = (p: PassPhase) => { if (!gone.current) setPhase(p); };
  const start = async (e: React.SyntheticEvent) => {
    if (!href && !mint) return;
    e.preventDefault();
    if (phase === "busy") return;
    set("busy");
    /* A minted link (CC 0321): asked for on the tap, so the wait covers the mint and the build.
       null = the server offers no pass here; the owner hides the control. */
    if (!href && mint) {
      let minted: string | null;
      try { minted = await mint(); } catch { set("failed"); return; }
      if (!minted) { set("idle"); return; }
      href = minted;
    }
    const link = href as string;
    let res: Response;
    try {
      /* Through the typed client (CC 0325): a plain GET, no custom header, default credentials, no referrer. */
      res = await manageSurface.downloadWalletPass(link);
    } catch {
      set("idle");
      window.location.assign(link);
      return;
    }
    if (!res.ok) { set("failed"); return; }
    if (opensWalletSheet()) {
      /* The Wallet sheet is the end state on a phone. */
      window.location.assign(link);
      window.setTimeout(() => set("idle"), 2500);
      return;
    }
    try {
      const blob = await res.blob();
      const url = URL.createObjectURL(new Blob([blob], { type: "application/vnd.apple.pkpass" }));
      const a = document.createElement("a");
      a.href = url; a.download = "pass.pkpass"; a.rel = "noreferrer";
      document.body.appendChild(a); a.click(); a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30000);
      set("done");
    } catch {
      set("failed");
    }
  };
  return { phase, start };
}

/** A small wait indicator, drawn beside the badge, never on it. */
const Spinner = () => (
  <span aria-hidden="true" style={{ width: 12, height: 12, borderRadius: 999, border: "2px solid #C4CAD4", borderTopColor: "#3A4254", display: "inline-block", animation: "wpb-spin 0.8s linear infinite", flexShrink: 0 }}>
    <style>{"@keyframes wpb-spin { to { transform: rotate(360deg); } }"}</style>
  </span>
);

export function WalletPassBadge(props: {
  /** The `.pkpass` download. Absent = the designer's inert preview: the badge is
   *  drawn (it is what the section can carry) but goes nowhere. */
  href?: string;
  /** Rendered instead when the artwork cannot be loaded. */
  fallback?: ReactNode;
  /** The line under the badge. Absent = no line. */
  note?: string;
  /** Mint the pass link on the tap instead of holding one (the manage page, CC 0321). Resolves null
   *  when the server offers no pass; rejects when it could not be minted (shown as the failure). */
  mint?: () => Promise<string | null>;
  /** "row" (CC 0338): the badge sits in a list row, its visible edge on the row's content line, the
   *  caption beside it (centred) where there is room and under it on a phone. The badge itself and
   *  its clear space are unchanged: the clear space sits in the row's padding. */
  layout?: "stack" | "row";
}): JSX.Element | null {
  const t = useT();
  const [failed, setFailed] = useState(false);
  const dl = usePassDownload(props.href, props.mint);
  const live = !!(props.href || props.mint);
  const keyStart = (e: React.KeyboardEvent) => { if (!props.href && (e.key === "Enter" || e.key === " ")) void dl.start(e); };
  const busy = dl.phase === "busy";
  /* The line under the control: the state when there is one, the authored note otherwise. */
  const stateLine = dl.phase === "busy"
    ? <span role="status" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "#3A4254", lineHeight: 1.5 }}><Spinner />{t("lib.walletPassBadge.busy", "Preparing your pass…")}</span>
    : dl.phase === "done"
    ? <span role="status" style={{ fontSize: 11.5, color: "#3A4254", lineHeight: 1.5 }}>{t("lib.walletPassBadge.done", "The pass was downloaded. Open it on your iPhone, or scan the code above.")}</span>
    : dl.phase === "failed"
    ? <span role="alert" style={{ fontSize: 11.5, color: "#B42318", lineHeight: 1.5 }}>{t("lib.walletPassBadge.failed", "We could not prepare the pass. Try again.")}</span>
    : null;
  /* The fallback stays a working download whenever there is a pass (CC 0038):
     the same anchor attributes as the badge's own. Without an href it is the
     designer's inert preview, as before. */
  if (failed) {
    if (!live) return <>{props.fallback ?? null}</>;
    return (
      <span style={{ display: "inline-flex", flexDirection: "column", gap: 4 }}>
        <a href={props.href} download rel="noreferrer" referrerPolicy="no-referrer" aria-label={t("lib.walletPassBadge.ariaLabel", "Add to Apple Wallet")}
          onClick={dl.start} onKeyDown={keyStart} role={props.href ? undefined : "button"} tabIndex={props.href ? undefined : 0} aria-busy={busy || undefined} aria-disabled={busy || undefined}
          style={{ display: "inline-flex", textDecoration: "none", color: "inherit", cursor: busy ? "progress" : "pointer" }}>
          {props.fallback ?? t("lib.walletPassBadge.text", "Add to Apple Wallet")}
        </a>
        {stateLine}
      </span>
    );
  }

  /* A FILE DOWNLOAD (application/vnd.apple.pkpass), not a navigation, hence
     `download`. The URL carries a management token in its query string, so it
     is treated as any credential-bearing URL: no analytics binding, no
     logging, referrer suppressed. */
  const badge = (
    <img
      src={badgeUrl(currentLocale())}
      onError={(e) => {
        /* Anything outside the 34 shipped locales falls back to en. Guarded so
           a missing en.svg cannot loop — and a missing en.svg is also how this
           component learns the artwork is not deployed at all, which is when
           `fallback` takes over. de-CH and qps-ploc are real files at those
           names (DE and US_UK artwork respectively — Apple ships no variant for
           either), so they resolve directly and no mapping table is needed. */
        const img = e.currentTarget;
        if (img.dataset.fallback) { setFailed(true); return; }
        img.dataset.fallback = "1";
        img.src = badgeUrl("en");
      }}
      alt=""
      height={BADGE_H}
      style={{ height: BADGE_H, width: "auto", maxWidth: "100%", display: "block" }}
    />
  );

  /* No filter, no transform, no hover recolour — a pressed or hover treatment
     belongs on the anchor, never on the artwork. An SVG optimiser on this path
     strips the badge's <style> block and it renders as a solid black slab:
     geometry intact, wordmark and logo invisible. That failure is invisible to
     reading the file and only shows up in a render. */
  const frame: React.CSSProperties = {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    padding: BADGE_H * 0.1, boxSizing: "content-box",
    maxWidth: "100%", textDecoration: "none",
  };

  const row = props.layout === "row";
  const clear = BADGE_H * 0.1;
  return (
    <span style={row
      ? { display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 12, rowGap: 4, marginLeft: -clear }
      : { display: "inline-flex", flexDirection: "column", gap: 4 }}>
      {live ? (
        <a href={props.href} download rel="noreferrer" referrerPolicy="no-referrer"
          onClick={dl.start} onKeyDown={keyStart} role={props.href ? undefined : "button"} tabIndex={props.href ? undefined : 0} aria-busy={busy || undefined} aria-disabled={busy || undefined}
          aria-label={t("lib.walletPassBadge.ariaLabel", "Add to Apple Wallet")} style={{ ...frame, cursor: busy ? "progress" : "pointer" }}>{badge}</a>
      ) : (
        <span role="img" aria-label={t("lib.walletPassBadge.ariaLabel", "Add to Apple Wallet")} style={frame}>{badge}</span>
      )}
      {row
        ? <span style={{ flex: "1 1 200px", minWidth: 0, paddingLeft: clear }}>{stateLine ?? (props.note ? <span style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.5 }}>{props.note}</span> : null)}</span>
        : (stateLine ?? (props.note ? (
          <span style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.5 }}>{props.note}</span>
        ) : null))}
    </span>
  );
}
